import React, { Suspense, useEffect, useRef, useState } from 'react';
import { __ } from 'i18n/index';
import { currentLadderUrl, HEADER_SECTIONS, sectionForPath } from 'react/layout/header/headerSections';
import { logout } from 'react/layout/header/logout';
import DevThemeSwitch from 'react/layout/header/DevThemeSwitch';
import PhoneHeader from 'react/layout/header/PhoneHeader';
import { isPlainLeftClick } from 'react/matchmaking/unified/header/plainLeftClick';

// The search and the notification bell need the Redux store, so they load in
// the browser after mounting.
const HeaderUserSearch = React.lazy(() => import(/* webpackChunkName: "HeaderUserSearch" */ 'react/layout/header/HeaderUserSearch'));
const HeaderNotificationBell = React.lazy(() => import(/* webpackChunkName: "HeaderNotificationBell" */ 'react/layout/header/HeaderNotificationBell'));

const MENU_TOGGLE_SELECTOR = '[data-header-menu-toggle]';

// False for the server render and the hydrating render, which must match it.
function useMounted() {
	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);
	return mounted;
}

// One dropdown open at a time. A click anywhere but a menu toggle, or Escape,
// closes it, as Bootstrap's dropdowns did.
function useOpenMenu() {
	const [openMenu, setOpenMenu] = useState(null);
	useEffect(() => {
		if(!openMenu) {
			return undefined;
		}
		const closeOnClick = (event) => {
			if(event.target instanceof Element && event.target.closest(MENU_TOGGLE_SELECTOR)) {
				return;
			}
			setOpenMenu(null);
		};
		const closeOnEscape = (event) => {
			if(event.key === 'Escape') {
				setOpenMenu(null);
			}
		};
		document.addEventListener('click', closeOnClick);
		document.addEventListener('keydown', closeOnEscape);
		return () => {
			document.removeEventListener('click', closeOnClick);
			document.removeEventListener('keydown', closeOnEscape);
		};
	}, [openMenu]);

	const toggleProps = (name) => ({
		'data-header-menu-toggle': name,
		'aria-expanded': openMenu === name,
		onClick: (event) => {
			event.preventDefault();
			setOpenMenu((current) => (current === name ? null : name));
		},
	});
	const openClass = (name) => (openMenu === name ? ' open' : '');
	return { toggleProps, openClass };
}

function Logo({ siteUrl, pageUrl, subOnly }) {
	if(subOnly) {
		return (
			<a href={`${pageUrl}/`} className='logo hidden-xs'>
				<img title='Rock Pika Scissors!' src={`${siteUrl}/images/rockchu.png`} />{' '}
				<span className='title_text hidden-sm'>VERYBETA<span className='accent'>SERVER</span></span>
			</a>
		);
	}
	return (
		<a href={`${pageUrl}/`} className='logo hidden-xs'>
			<img src={`${siteUrl}/images/logos/smashladder_pikamask_covid.png`} />{' '}
			<span className='title_text hidden-sm'>
				Smash<span className='apostrophe'></span><span className='accent'>Ladder</span>
			</span>
		</a>
	);
}

function MenuRow({ href, icon, label, className, linkClassName }) {
	return (
		<li className={className}>
			<a href={href} className={linkClassName}>
				<span className='ico'><i className={`fa ${icon}`}></i></span>{label}
			</a>
		</li>
	);
}

function Divider() {
	return <li className='header-menu__divider' role='separator'></li>;
}

function GroupHeading({ children }) {
	return <li className='header-menu__group-hdr'>{children}</li>;
}

function RanksMenu({ ranks, path, active, menus }) {
	const ladderUrl = currentLadderUrl(path);
	const groups = [
		{ label: __('Your ladders'), ladders: ranks.yours },
		{ label: __('All ladders'), ladders: ranks.others },
	].filter((group) => group.ladders.length > 0);

	return (
		<li className={`dropdown header-menu header-menu--ranks${menus.openClass('ranks')}`}>
			<a className={`dropdown-toggle${active ? ' active' : ''}`} role='button' {...menus.toggleProps('ranks')}>
				<span className='main_menu_label'>{__('Ranks')}</span> <i className='fa fa-chevron-down'></i>
			</a>
			<ul className='dropdown-menu' role='menu'>
				{groups.map((group, index) => (
					<React.Fragment key={group.label}>
						{index > 0 && <Divider />}
						<GroupHeading>{group.label}</GroupHeading>
						{group.ladders.map((ladder) => {
							const isCurrent = ladderUrl !== null && ladder.url === ladderUrl;
							return (
								<li key={ladder.url} className={isCurrent ? 'header-menu__row--active' : undefined}>
									<a href={ladder.rankingsUrl}>
										<img className='header-menu__game-icon' alt='' src={ladder.iconUrl} />{' '}
										{ladder.name}
										{isCurrent && <React.Fragment>{' '}<i className='fa fa-check header-menu__row-aside'></i></React.Fragment>}
									</a>
								</li>
							);
						})}
					</React.Fragment>
				))}
			</ul>
		</li>
	);
}

function MoreMenu({ pageUrl, menus }) {
	return (
		<li className={`dropdown header-menu header-menu--more${menus.openClass('more')}`}>
			<a className='dropdown-toggle' role='button' {...menus.toggleProps('more')}>
				<span className='main_menu_label'>{__('More')}</span> <i className='fa fa-chevron-down'></i>
			</a>
			<ul className='dropdown-menu' role='menu'>
				<GroupHeading>{__('Browse')}</GroupHeading>
				<MenuRow href={`${pageUrl}/stats`} linkClassName='netplay_server_down' icon='fa-line-chart' label={__('Cool Stats')} />
				<MenuRow href={`${pageUrl}/help/terms-and-conditions`} linkClassName='netplay_server_down' icon='fa-file-text-o' label={__('Site Rules')} />
				<Divider />
				<GroupHeading>{__('Help us improve')}</GroupHeading>
				<MenuRow href={`${pageUrl}/bugs/submit?type=2`} icon='fa-bug' label={__('Report a bug')} />
				<MenuRow href={`${pageUrl}/bugs/submit?type=3`} icon='fa-comment-o' label={__('Send feedback')} />
				<MenuRow href={`${pageUrl}/bugs/submit?type=1`} icon='fa-lightbulb-o' label={__('Request a feature')} />
			</ul>
		</li>
	);
}

// Each choice posts the page's path (without a locale prefix), so the switch
// lands on the same page in that locale (Locale_Controller::set).
function LocaleSwitcher({ locales, path, menus }) {
	return (
		<li className={`dropdown header-menu header-menu--locale header-menu--right locale-switcher${menus.openClass('locale')}`}>
			<a className='dropdown-toggle' href='#' role='button' {...menus.toggleProps('locale')}>
				<i className='fa fa-globe'></i>{' '}
				<span className='hidden-xs hidden-md' lang={locales.current}>{locales.currentLabel}</span>{' '}
				<i className='fa fa-chevron-down'></i>
			</a>
			<ul className='dropdown-menu' role='menu'>
				<GroupHeading>{__('Language')}</GroupHeading>
				{locales.options.map((option) => {
					const isActive = option.code === locales.current;
					return (
						<li key={option.code} className={isActive ? 'active' : ''}>
							<form method='POST' action={locales.action} className='locale-switcher-form'>
								<input type='hidden' name='locale' value={option.code} />
								{locales.returnField && <input type='hidden' name={locales.returnField} value={path || '/'} />}
								<button type='submit' className='locale-switcher-option'>
									<span className='locale-switcher-code'>{option.code.slice(0, 2).toUpperCase()}</span>{' '}
									<span lang={option.code}>{option.label}</span>
									{isActive && <React.Fragment>{' '}<span className='header-menu__row-aside'><i className='fa fa-check'></i></span></React.Fragment>}
								</button>
							</form>
						</li>
					);
				})}
			</ul>
		</li>
	);
}

function UserMenu({ viewer, pageUrl, onLogOut, devThemeSwitch, section, menus }) {
	return (
		<li className={`dropdown header-menu header-menu--user header-menu--right${menus.openClass('user')}`}>
			<a className='dropdown-toggle' href={viewer.profileUrl} role='button' {...menus.toggleProps('user')}>
				<span data-id={viewer.id} className={`${viewer.usernameClasses} unclickable`}>
					<span className='hidden-xs hidden-sm'>{viewer.displayName}</span>{' '}
					<span className=' hidden-md hidden-lg'>{__('You')} <i className='fa fa-chevron-down'></i></span>{' '}
					<span><i className='hidden-md hidden-sm hidden-xs fa fa-chevron-down'></i></span>
				</span>
			</a>
			<ul className='dropdown-menu profile_menu' role='menu'>
				<GroupHeading>{__('Account')}</GroupHeading>
				<MenuRow
					href={viewer.profileUrl}
					className={section === HEADER_SECTIONS.ownProfile ? 'header-menu__row--active' : undefined}
					icon='fa-user-o'
					label={__('Profile')}
				/>
				<MenuRow href={viewer.accountSettingsUrl} icon='fa-cog' label={__('Account Settings')} />
				<MenuRow href={viewer.preferredGamesUrl} className='game_settings_selector' icon='fa-gamepad' label={__('Preferred Games')} />
				<Divider />
				<GroupHeading>{__('Customize')}</GroupHeading>
				<MenuRow href={viewer.blogUrl} icon='fa-pencil-square-o' label={__('Blog')} />
				{devThemeSwitch && <DevThemeSwitch initialTheme={devThemeSwitch.theme} />}
				{viewer.canSeeModMenu && (
					<React.Fragment>
						<Divider />
						<GroupHeading>{__('Mod')}</GroupHeading>
						<MenuRow href={`${pageUrl}/mod`} icon='fa-gavel' label={__('Moderation')} />
					</React.Fragment>
				)}
				{viewer.isImpersonating && (
					<React.Fragment>
						<Divider />
						<li>
							<a className='stop_impersonating_button logout_button' onClick={onLogOut}>
								<span className='ico'><i className='fa fa-user-secret'></i></span>{__('Stop Impersonating')}
							</a>
						</li>
					</React.Fragment>
				)}
				<Divider />
				<li>
					<a id='logout_button' className='logout_button' onClick={onLogOut}>
						<span className='ico'><i className='fa fa-sign-out'></i></span>{__('Logout')}
					</a>
				</li>
			</ul>
		</li>
	);
}

function NotifyRow({ viewer, menus }) {
	return (
		<ul className='nav top-menu'>
			<li id='header_inbox_bar' className={`dropdown${menus.openClass('inbox')}`}>
				<a className='dropdown-toggle' href={viewer.inboxUrl} {...menus.toggleProps('inbox')}>
					<i className='fa fa-envelope-o'></i>
					<span className='badge bg-important new_inbox_messages'></span>
				</a>
				<ul id='recent_private_chats' className='dropdown-menu extended inbox pull-right' style={{ maxHeight: '450px', overflowY: 'auto' }}>
					<li id='new_message_count_container'>
						<p className='red'>You have <span className='new_inbox_messages'>---</span> new message<span className='the_s'>s</span></p>
					</li>
					<li>
						<a href={viewer.inboxUrl}>{__('See all messages')}</a>
					</li>
				</ul>
			</li>
		</ul>
	);
}

// The site header on every page that uses the flatlab layout. header is the
// props services\layout\SiteHeaderProps builds; path is the page's path, which
// picks the active section (an app shell passes its router's location).
// onNetplayNavigate, when the netplay app is on the page, switches Chat/Play in place. The data-netplay-status slots are
// filled by the netplay app (NetplayHeaderBridge); the header leaves them empty.
export default function SiteHeader({ header, path, onNetplayNavigate = null }) {
	const mounted = useMounted();
	const menus = useOpenMenu();
	const logoutFormRef = useRef(null);
	const logOut = (event) => {
		event.preventDefault();
		logout(logoutFormRef.current);
	};
	const { viewer, siteUrl } = header;
	// Page links keep the request's locale prefix (/ja); images use siteUrl.
	const pageUrl = header.pageUrl || siteUrl;
	const section = sectionForPath(path, viewer ? viewer.profilePath : null);

	const netplayLinkClick = (view) => (event) => {
		if(!isPlainLeftClick(event)) {
			return;
		}
		if(onNetplayNavigate) {
			event.preventDefault();
			onNetplayNavigate(view);
		}
	};

	return (
		<header id='header' className='header'>
			<div className='header_content'>
				<Logo siteUrl={siteUrl} pageUrl={pageUrl} subOnly={header.subOnly} />
				<div id='top_main_nav'>
					<ul className='main_menu_items nav navbar-nav'>
						{viewer && (
							<React.Fragment>
								<li className='netplay_nav_link chat_link'>
									<a
										className={section === HEADER_SECTIONS.chat ? 'active' : ''}
										aria-current={section === HEADER_SECTIONS.chat ? 'page' : undefined}
										data-netplay-view='chat'
										href={`${pageUrl}/netplay/chat`}
										onClick={netplayLinkClick('chat')}
									>
										<span className='main_menu_label'>{__('Chat')}</span><span className='netplay-nav-status-slot' data-netplay-status='chat'></span>
									</a>
								</li>
								<li className='sub-menu dcjq-parent-li matchmaking_link netplay_nav_link'>
									<a
										className={section === HEADER_SECTIONS.play ? 'active' : ''}
										aria-current={section === HEADER_SECTIONS.play ? 'page' : undefined}
										data-netplay-view='play'
										href={`${pageUrl}/netplay`}
										onClick={netplayLinkClick('play')}
									>
										<span className='main_menu_label'>{__('Play')}</span><span className='netplay-nav-status-slot' data-netplay-status='play'></span>
									</a>
								</li>
							</React.Fragment>
						)}
						{header.showSiteLinks && (
							<React.Fragment>
								{!viewer && (
									<React.Fragment>
										<li>
											<a
												className={section === HEADER_SECTIONS.logIn ? 'active' : ''}
												aria-current={section === HEADER_SECTIONS.logIn ? 'page' : undefined}
												href={`${pageUrl}/log-in`}
											>
												<span className='main_menu_label'><i className='fa fa-user'></i>{' '}<span>{__('Log-in')}</span></span>
											</a>
										</li>
										<li>
											<a
												className={section === HEADER_SECTIONS.signUp ? 'active' : ''}
												aria-current={section === HEADER_SECTIONS.signUp ? 'page' : undefined}
												href={`${pageUrl}/sign-up`}
											>
												<span className='main_menu_label'><i className='fa fa-user'></i>{' '}<span>{__('Sign-Up')}</span></span>
											</a>
										</li>
									</React.Fragment>
								)}
								<RanksMenu ranks={header.ranks} path={path} active={section === HEADER_SECTIONS.ranks} menus={menus} />
							</React.Fragment>
						)}
						{viewer && (
							<React.Fragment>
								<li>
									<a
										className={`${section === HEADER_SECTIONS.subscribe ? 'active ' : ''}emphasized_bright`}
										aria-current={section === HEADER_SECTIONS.subscribe ? 'page' : undefined}
										href={`${pageUrl}/subscribe`}
									>
										<span className='main_menu_label'>
											<span className='hidden-sm'>{__('Subscribe')}</span>
											<span className='hidden-lg hidden-md hidden-xs'>{__('Sub')}</span>
										</span>
									</a>
								</li>
								<MoreMenu pageUrl={pageUrl} menus={menus} />
							</React.Fragment>
						)}
					</ul>
				</div>
				<div className='top-nav '>
					<ul className='nav pull-right top-menu'>
						{viewer && (
							<li id='search_dropdown_retro'>
								{mounted && (
									<Suspense fallback={null}>
										<HeaderUserSearch />
									</Suspense>
								)}
							</li>
						)}
						{viewer && (
							<li id='header_notification_bell'>
								{mounted && (
									<Suspense fallback={null}>
										<HeaderNotificationBell modAccess={{ isMod: viewer.isMod, isLadderMod: viewer.isLadderMod }} />
									</Suspense>
								)}
							</li>
						)}
						{header.locales && <LocaleSwitcher locales={header.locales} path={path} menus={menus} />}
						{viewer && <UserMenu viewer={viewer} pageUrl={pageUrl} onLogOut={logOut} devThemeSwitch={header.devThemeSwitch} section={section} menus={menus} />}
					</ul>
					<div className='nav notify-row' id='top_menu'>
						{viewer && <NotifyRow viewer={viewer} menus={menus} />}
					</div>
				</div>
				{/* Last in the markup so the desktop nav's links come first; PhoneHeader.scss places it. */}
				<PhoneHeader
					header={header}
					pageUrl={pageUrl}
					path={path}
					section={section}
					netplayLinkClick={netplayLinkClick}
					onLogOut={logOut}
				/>
			</div>
			{viewer && header.logout && (
				<form method='POST' style={{ display: 'none' }} id='logout_form' action={header.logout.action} ref={logoutFormRef}>
					<input type='hidden' name='logout' value='1' />
					<input type='hidden' name={header.logout.csrf.name} value={header.logout.csrf.value} />
				</form>
			)}
		</header>
	);
}
